/**
 * Human-friendly byte formatting for storage quotas.
 * All inputs treated as bigint (bytes) to keep >2^53 precision for TB-scale.
 */
export function formatBytes(input: string | bigint | number): string {
  const b = typeof input === 'bigint' ? input : BigInt(input);
  if (b === 0n) return '0 B';
  const KB = 1024n;
  const MB = KB * 1024n;
  const GB = MB * 1024n;
  const TB = GB * 1024n;
  if (b >= TB) return `${(Number(b * 100n / TB) / 100).toFixed(2)} TB`;
  if (b >= GB) return `${(Number(b * 100n / GB) / 100).toFixed(2)} GB`;
  if (b >= MB) return `${(Number(b * 100n / MB) / 100).toFixed(2)} MB`;
  if (b >= KB) return `${(Number(b * 100n / KB) / 100).toFixed(2)} KB`;
  return `${b} B`;
}

/** Parse "5 GB" / "500MB" / "1073741824" — case-insensitive */
export function parseBytesInput(input: string): bigint | null {
  const s = input.trim().toUpperCase().replace(/\s+/g, '');
  const m = s.match(/^(\d+(?:\.\d+)?)(TB|GB|MB|KB|B)?$/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (isNaN(n) || n < 0) return null;
  const unit = m[2] || 'B';
  const mul: Record<string, bigint> = {
    B: 1n,
    KB: 1024n,
    MB: 1024n * 1024n,
    GB: 1024n * 1024n * 1024n,
    TB: 1024n * 1024n * 1024n * 1024n,
  };
  const scaled = BigInt(Math.round(n * 1000));
  return (scaled * mul[unit]) / 1000n;
}

/** Colour band for usage percent */
export type UsageBand = 'ok' | 'warn' | 'critical' | 'over';
export function usageBand(percent: number): UsageBand {
  if (percent >= 100) return 'over';
  if (percent >= 95) return 'critical';
  if (percent >= 80) return 'warn';
  return 'ok';
}
